<template>
    <FormSection
        id="Demographic_Information"
        title="Demographic Information"
        :type="`${isHisSection ? 'his-section' : 'default-section'}`"
        :full-height="false"
        :application-id="referral.application"
    >
        <template #content>
            <div class="row px-4 py-2 pb-0 g-0">
                <div class="col-12 offset-lg-1 col-lg-10 mb-1 row g-0">
                    <div class="col-12">
                        <h6 class="mb-2 d-flex align-items-center">
                            <b class="me-2">Patient Information</b>
                        </h6>
                    </div>
                    <div class="form-group col-12 col-lg-3 pe-lg-2">
                        <label id="Referral_First_Name_Label" for="referral-first-name">
                            <span class="badge me-1" v-if="isHisSection">A0500A</span> First Name
                            <span class="text-danger">*</span>
                        </label>
                        <input
                            id="Referral_First_Name"
                            name="referral-first-name"
                            class="form-control"
                            maxlength="50"
                            type="text"
                            placeholder="Enter First Name"
                            v-model="referral.firstName"
                            @blur="v$.referral.firstName.$touch"
                            :class="`${!v$.referral.firstName.$error ? '' : 'field-invalid'}`"
                        />
                        <span v-if="v$.referral.firstName.$error" class="field-invalid">First Name is required.</span>
                    </div>
                    <div class="form-group col-4 col-lg-2 pe-lg-2">
                        <label for="referral-middle-initial">
                            <span class="badge me-1" v-if="isHisSection">A0500B</span> {{ middleLabel }}
                        </label>
                        <input
                            name="referral-middle-initial"
                            class="form-control"
                            :maxlength="isApplicationPalliative ? 100 : 1"
                            type="text"
                            :placeholder="isApplicationPalliative ? 'Enter Middle Name' : 'Enter M.I.'"
                            v-model="middleName"
                        />
                    </div>
                    <div class="form-group col-12 col-lg-3 pe-lg-2">
                        <label id="Referral_Last_Name_Label" for="referral-last-name">
                            <span class="badge me-1" v-if="isHisSection">A0500C</span> Last Name
                            <span class="text-danger">*</span>
                        </label>
                        <input
                            id="Referral_Last_Name"
                            name="referral-last-name"
                            class="form-control"
                            maxlength="50"
                            placeholder="Enter Last Name"
                            type="text"
                            v-model="referral.lastName"
                            @blur="v$.referral.lastName.$touch"
                            :class="`${!v$.referral.lastName.$error ? '' : 'field-invalid'}`"
                        />
                        <span v-if="v$.referral.lastName.$error" class="field-invalid">Last Name is required.</span>
                    </div>
                    <div class="form-group col-4 col-lg-2 pe-lg-2">
                        <label for="referral-suffix">
                            <span class="badge me-1" v-if="isHisSection">A0500D</span> Suffix
                        </label>
                        <input
                            name="referral-suffix"
                            class="form-control"
                            maxlength="4"
                            type="text"
                            placeholder="Enter Suffix"
                            v-model="referral.suffix"
                        />
                    </div>
                    <div class="w-100"></div>
                    <div class="form-group col-12 col-lg-3 pe-lg-2">
                        <label for="date-of-birth" id="Referral_Date_Of_Birth_Label">
                            <span class="badge me-1" v-if="isHisSection">A0900</span> Date of Birth
                            <span class="text-danger">*</span>
                        </label>
                        <DatePicker :getDate="getDate">
                            <div class="input-group date datepickerInput">
                                <input
                                    name="dateOfBirth"
                                    id="Referral_Date_Of_Birth"
                                    type="text"
                                    class="form-control"
                                    placeholder="MM/DD/YYYY"
                                    :value="formatDateInput(referral.dateOfBirth)"
                                    :on-change="(e: any) => getDate(e.target.value, e.target.name)"
                                    autocomplete="off"
                                    @blur="v$.referral.dateOfBirth.$touch"
                                    :class="`${!v$.referral.dateOfBirth.$error ? '' : 'field-invalid'}`"
                                />
                                <div class="input-group-addon input-group-append">
                                    <div class="input-group-text">
                                        <i class="fa fa-calendar"></i>
                                    </div>
                                </div>
                                <span v-if="v$.referral.dateOfBirth.$error" class="field-invalid"
                                    >Date of Birth is required.</span
                                >
                            </div>
                        </DatePicker>
                    </div>
                    <div class="form-group col-12 col-lg-2 pe-lg-2">
                        <label id="Referral_Gender_Label" for="referral-gender">
                            <span class="badge me-1" v-if="isHisSection">A0800</span> Sex
                            <span class="text-danger">*</span>
                        </label>
                        <select
                            id="Referral_Gender"
                            class="form-select"
                            name="referral-gender"
                            v-model="referral.gender"
                            @blur="v$.referral.gender.$touch"
                            :class="`${!v$.referral.gender.$error ? '' : 'field-invalid'}`"
                        >
                            <option :value="0">Select Sex</option>
                            <option
                                v-for="value of Genders"
                                :value="value.value"
                                v-show="!(!isApplicationPalliative && value.value == genderUnknownValue)"
                            >
                                {{ value.name }}
                            </option>
                        </select>
                        <span v-if="v$.referral.gender.$error" class="field-invalid">Sex is required.</span>
                    </div>
                    <template v-if="isApplicationPalliative">
                        <div class="form-group col-12 col-lg-3 pe-lg-2">
                            <label id="Referral_Gender_Identity_Label" for="referral-gender-identity">
                                Sex Identity
                            </label>
                            <select
                                id="Referral_Gender_Identity"
                                class="form-select"
                                name="referral-gender-identity"
                                v-model="referral.genderIdentity"
                            >
                                <option :value="0">Select Sex Identity</option>
                                <option v-for="[key, value] of GenderIdentities" :value="value.value">
                                    {{ value.name }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group col-12 col-lg-3 pe-lg-2" v-if="referral.genderIdentity === 6">
                            <label id="Referral_Other_Gender_Identity_Label" for="referral-other-gender-identity">
                                Please specify
                                <span class="text-danger">*</span>
                            </label>
                            <input
                                id="Referral_Other_Gender_Identity"
                                name="referral-other-gender-identity"
                                class="form-control"
                                maxlength="255"
                                type="text"
                                v-model="referral.otherGenderIdentity"
                                @blur="v$.referral.otherGenderIdentity.$touch"
                                :class="`${!v$.referral.otherGenderIdentity.$error ? '' : 'field-invalid'}`"
                            />
                            <span v-if="v$.referral.otherGenderIdentity.$error" class="field-invalid"
                                >OtherGenderIdentity is required.</span
                            >
                        </div>
                    </template>
                    <template v-if="isApplicationPalliative">
                        <div class="form-group col-12 col-lg-3 pe-lg-2">
                            <label id="Referral_Sexual_Orientation_Label" for="referral_sexual_orientation">
                                Sexual Orientation
                            </label>
                            <select
                                id="Referral_Sexual_Orientation"
                                class="form-select"
                                name="referral_sexual_orientation"
                                v-model="referral.sexualOrientation"
                            >
                                <option :value="0">Select Sexual Orientation</option>
                                <option v-for="[key, value] of SexualOrientation" :value="value.value">
                                    {{ value.name }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group col-12 col-lg-3 pe-lg-2" v-if="referral.sexualOrientation === 4">
                            <label id="Referral_Other_Sexual_Orientation_Label" for="referral-other-sexual-orientation">
                                Please specify
                                <span class="text-danger">*</span>
                            </label>
                            <input
                                id="Referral_Other_Sexual_Orientation"
                                name="referral-other-sexual-orientation"
                                class="form-control"
                                maxlength="255"
                                type="text"
                                v-model="referral.otherSexualOrientation"
                                @blur="v$.referral.otherSexualOrientation.$touch"
                                :class="`${!v$.referral.otherSexualOrientation.$error ? '' : 'field-invalid'}`"
                            />
                            <span v-if="v$.referral.otherSexualOrientation.$error" class="field-invalid"
                                >OtherSexualOrientation is required.</span
                            >
                        </div>
                    </template>

                    <div class="form-group col-12 col-lg-3 pe-lg-2">
                        <label for="referral-social-security-number">
                            <span class="badge me-1" v-if="isHisSection">A0600A</span> Social Security Number
                        </label>
                        <input
                            name="referral-social-security-number"
                            maxlength="9"
                            class="form-control"
                            type="text"
                            placeholder="Enter SSN"
                            v-model="referral.socialSecurityNumber"
                            v-number-only
                            @blur="v$.referral.socialSecurityNumber.$touch"
                            :class="`${!v$.referral.socialSecurityNumber.$error ? '' : 'field-invalid'}`"
                        />
                        <span v-if="v$.referral.socialSecurityNumber.$error" class="field-invalid">{{
                            v$.referral.socialSecurityNumber.$errors[0].$message
                        }}</span>
                    </div>
                    <div class="form-group col-4 col-lg-2 pe-lg-2">
                        <label for="referral-veteran"> Veteran </label>
                        <div class="input-group d-flex w-100 flex-nowrap">
                            <div class="input-group-text form-check custom-checkbox">
                                <input type="checkbox" class="" id="veteran" v-model="isVeteranSelected" />
                                <label class="form-check-label mb-3" for="veteran"></label>
                            </div>
                            <div class="flex-shrink-1 flex-grow-1">
                                <MultiSelectDropdown
                                    :disabled="!isVeteranSelected"
                                    :options="veteranOptions"
                                    :selected-items="referral.veteran"
                                    v-on:selectedItemsUpdated="selectedItemsUpdated"
                                />
                            </div>
                        </div>
                    </div>
                    <template v-if="isApplicationPalliative">
                        <div class="form-group col-12 col-lg-3 pe-lg-2">
                            <label for="task"> Occupation </label>
                            <TypeaheadInput
                                :getData="getOccupationList"
                                :value="occupation"
                                v-on:valueChanged="occupationChanged"
                                :open-on-focus="true"
                                :must-select-value="true"
                                placeholder="Select Occupation"
                                :closeOnOutsideClick="true"
                            />
                        </div>
                        <div class="form-group col-12 col-lg-3 pe-lg-2">
                            <label for="task"> Occupation Industry</label>
                            <TypeaheadInput
                                :getData="getIndustryList"
                                :value="industry"
                                v-on:valueChanged="industryChanged"
                                :show-all-by-default="true"
                                :open-on-focus="true"
                                :must-select-value="true"
                                placeholder="Select Occupation Industry"
                                :closeOnOutsideClick="true"
                            />
                        </div>
                    </template>
                    <div class="w-100"></div>
                    <div class="form-group col-12 col-lg-4 pe-lg-2" v-if="arePhoneNumbersAvailable">
                        <label for="referral-mobile-number">
                            Primary Phone Number
                            <i class="fas fa-question-circle" v-tooltip="{ title: tooltip }"></i>
                            <span class="text-danger">*</span>
                        </label>
                        <PhoneNumber
                            :enable-country="true"
                            v-model:value="primaryNumber"
                            v-model:extension="primaryExtension"
                            v-model:type="primaryType"
                            v-model:country="primaryCountry"
                            :phone-type-options="phoneNumberTypeOptionsConfig"
                            v-number-only
                            v-on:blurredInput="v$.referral.phoneNumbers.$touch()"
                            :class="`${
                                !v$.referral.phoneNumbers?.phoneNumber.$invalid ||
                                !v$.referral.phoneNumbers?.phoneType.$invalid
                                    ? ''
                                    : 'field-invalid'
                            }`"
                        />
                        <span v-if="v$.referral.phoneNumbers?.$error">
                            <span
                                v-if="
                                    v$.referral.phoneNumbers?.phoneNumber.$invalid &&
                                    v$.referral.phoneNumbers?.phoneType.$invalid
                                "
                                class="field-invalid"
                                >Phone Number and Phone Type is required.</span
                            >
                            <span v-else-if="v$.referral.phoneNumbers?.phoneNumber.$invalid">
                                <span
                                    v-if="v$.referral.phoneNumbers?.$errors[0].$message == 'Value is required'"
                                    class="field-invalid"
                                    >Phone Number is required.</span
                                >
                                <span v-else class="field-invalid">10 Digit phone is required.</span>
                            </span>
                            <span v-else-if="v$.referral.phoneNumbers?.phoneType.$invalid" class="field-invalid"
                                >Phone Type is required.</span
                            >
                        </span>
                    </div>
                    <div class="form-group col-12 col-lg-4 pe-lg-2" v-if="arePhoneNumbersAvailable">
                        <label for="referral-alternate-phone"> Alternate Phone Number </label>
                        <PhoneNumber
                            :enable-country="true"
                            v-model:value="alternateNumber"
                            v-model:extension="alternateExtension"
                            v-model:type="alternateType"
                            v-model:country="alternateCountry"
                            :phone-type-options="phoneNumberTypeOptionsConfig"
                            v-number-only
                        />
                    </div>
                    <div class="form-group col-12 col-lg-4 pe-lg-2">
                        <label for="referral-email"> Email </label>
                        <input
                            name="referral-email"
                            class="form-control"
                            maxlength="50"
                            type="text"
                            placeholder="Enter Email"
                            v-model="referral.email"
                        />
                    </div>
                    <div class="w-100"></div>
                    <div class="form-group col-12 col-lg-4 pe-lg-2">
                        <label id="Referral_Agency_Branch_Label" for="agency-location-id">
                            Agency Branch
                            <span class="text-danger">*</span>
                        </label>
                        <select
                            id="Referral_Agency_Branch"
                            name="agency-location-id"
                            class="form-select"
                            v-model="referral.locationId"
                            @blur="v$.referral.locationId.$touch"
                            :class="`${!v$.referral.locationId.$error ? '' : 'field-invalid'}`"
                        >
                            <option :value="null">Select Branch</option>
                            <option v-for="branch of branches" :value="branch.id">{{ branch.name }}</option>
                        </select>
                        <span v-if="v$.referral.locationId.$error" class="field-invalid"
                            >Agency Branch is required.</span
                        >
                    </div>
                    <div class="form-group col-12 col-lg-4 pe-lg-2">
                        <label for="referral-marital-status"> Marital Status </label>
                        <select class="form-select" name="marital-status" v-model="referral.maritalStatus">
                            <option :value="0">Select Marital Status</option>
                            <option v-for="[key, value] of MaritalStatuses" :key="key" :value="value.value">
                                {{ value.name }}
                            </option>
                        </select>
                    </div>
                    <div class="form-group col-12 col-lg-4 pe-lg-2" v-if="isApplicationPalliative">
                        <label for="referral-ethnicity"> Ethnicity <span class="text-danger">*</span></label>
                        <select
                            id="referral-ethnicity"
                            class="form-select"
                            name="ethnicityPalliative"
                            v-model="referral.ethnicityOnly"
                            :disabled="isEthnicityPalliativeDeclined"
                            @blur="v$.referral.ethnicityOnly.$touch"
                        >
                            <option :value="null">Select Ethnicity</option>
                            <option v-for="[key, value] of EthnicityPalliative" :value="value.value">
                                {{ value.name }}
                            </option>
                        </select>
                        <div class="form-check custom-checkbox pt-1">
                            <input
                                type="checkbox"
                                class="form-check-input pb-1"
                                id="decline-ethnicity"
                                v-model="isEthnicityPalliativeDeclined"
                            />
                            <label class="form-check-label" for="decline-ethnicity">Declines to Specify</label>
                        </div>
                        <span v-if="v$.referral.ethnicityOnly.$error" class="field-invalid"
                            >Ethnicity is required.</span
                        >
                    </div>
                    <div class="w-100"></div>
                    <template v-if="isHisSection">
                        <div v-if="loading"><span class="shimmer-loading"></span></div>
                        <div class="form-group col-12 col-lg-6 pe-lg-2 race-section" v-else>
                            <label for="referral-race">
                                <span class="badge me-1">A1000</span> Race <span class="text-danger">*</span></label
                            >
                            <TagsInput
                                class="tags-wrapper-hos"
                                :data="RacesHospice"
                                :tags="selectedRacesHospice"
                                placeholder="Select Race"
                                aria-autocomplete="off"
                                :open-on-focus="true"
                                v-on:updateTags="updateSelectedHospiceRaces"
                                v-on:blurredInput="v$.referral.race.$touch()"
                            ></TagsInput>
                            <span v-if="v$.referral.race.$error" class="field-invalid">Race is required.</span>
                        </div>
                        <div v-if="loading"><span class="shimmer-loading"></span></div>
                        <div class="form-group col-12 col-lg-6 pe-lg-2" v-else>
                            <label for="ethnicityHospice">
                                <span class="badge me-1">A1000</span> Ethnicity
                                <span class="text-danger">*</span></label
                            >
                            <TagsInput
                                class="tags-wrapper-hos"
                                :data="EthnicityHospice"
                                :tags="selectedEthnicityHospice"
                                placeholder="Select Ethnicity"
                                aria-autocomplete="off"
                                :open-on-focus="true"
                                v-on:updateTags="updateSelectedHospiceEthnictiy"
                                v-on:blurredInput="v$.referral.hispanicEthnicityValues.$touch()"
                            />
                            <span v-if="v$.referral.hispanicEthnicityValues.$error" class="field-invalid"
                                >Ethnicity is required.</span
                            >
                        </div>
                    </template>
                    <template v-else>
                        <div class="w-100"></div>
                        <div class="form-group col-12 col-lg-3 pe-lg-2">
                            <label for="referral-race"> Race </label>
                            <TagsInput
                                :data="RacesPalliative"
                                :tags="selectedRacesPalliative"
                                v-on:updateTags="updateSelectedRaces"
                                placeholder="Select Race"
                                aria-autocomplete="off"
                                :open-on-focus="true"
                                :disabled="isRacesPalliativeDeclined"
                            />
                            <div class="form-check custom-checkbox pt-1">
                                <input
                                    type="checkbox"
                                    class="form-check-input pb-1"
                                    id="decline-race"
                                    v-model="isRacesPalliativeDeclined"
                                />
                                <label class="form-check-label" for="decline-race">Declines to Specify</label>
                            </div>
                        </div>
                        <div class="form-group col-12 col-lg-3 pe-lg-2">
                            <label for="task"> More Granular Race </label>
                            <TypeaheadInput
                                dropdownMenuClass="granular-race-dropdown"
                                :getData="moreGranularRaceFilter"
                                :value="granularRace"
                                v-on:valueChanged="granularRaceChanged"
                                :show-all-by-default="true"
                                :open-on-focus="true"
                                :must-select-value="true"
                                placeholder="Type to Filter Race"
                                :closeOnOutsideClick="true"
                                :disabled="isRacesPalliativeDeclined"
                            />
                        </div>
                        <div class="form-group col-12 col-lg-2 pe-lg-2">
                            <label for="tribeIdentification"> Patient identifies with a tribe </label>
                            <div class="col-12 d-flex px-0">
                                <div class="custom-control custom-radio custom-control-inline pe-lg-2">
                                    <input
                                        type="radio"
                                        id="yesTribe"
                                        name="tribeIdentification"
                                        class="custom-control-input"
                                        v-model="referral.isIdentifyWithTribe"
                                        :value="true"
                                        :disabled="hasDeclinedToIdentifyTribe"
                                    />
                                    <label class="custom-control-label" for="yesTribe"> Yes </label>
                                </div>
                                <div class="custom-control custom-radio custom-control-inline">
                                    <input
                                        type="radio"
                                        id="noTribe"
                                        name="tribeIdentification"
                                        class="custom-control-input"
                                        v-model="referral.isIdentifyWithTribe"
                                        :value="false"
                                        :disabled="hasDeclinedToIdentifyTribe"
                                    />
                                    <label class="custom-control-label" for="noTribe"> No </label>
                                </div>
                            </div>
                            <div class="custom-control custom-checkbox pt-1">
                                <input
                                    type="checkbox"
                                    class="custom-control-input pb-1"
                                    id="decline-tribe"
                                    :checked="referral.hasDeclinedToIdentifyTribe"
                                    @change="handleDeclineChange($event.target.checked)"
                                />
                                <label class="custom-control-label" for="decline-tribe">Declines to Specify</label>
                            </div>
                        </div>
                        <div
                            class="form-group col-12 col-lg-2 pe-lg-2"
                            v-if="referral.isIdentifyWithTribe === true && !hasDeclinedToIdentifyTribe"
                        >
                            <label for="referral-tribal-affiliation">
                                Tribal Affiliation <span class="text-danger">*</span></label
                            >
                            <select
                                class="form-select"
                                name="tribal-affiliation"
                                v-model="referral.typeOfAffiliation"
                                @blur="v$.referral.typeOfAffiliation.$touch"
                                :class="`${!v$.referral.typeOfAffiliation.$error ? '' : 'field-invalid'}`"
                            >
                                <option :value="null">Select Tribal Affiliation</option>
                                <option v-for="[key, value] of TribalAffiliation" :key="key" :value="value.value">
                                    {{ value.name }}
                                </option>
                            </select>
                            <span v-if="v$.referral.typeOfAffiliation.$error" class="field-invalid"
                                >Tribal Affiliation is required.</span
                            >
                        </div>
                        <div class="form-group col-12 col-lg-2 pt-1 pe-lg-2" v-if="referral.typeOfAffiliation == 5">
                            <label class="pt-2"></label>
                            <input
                                name="referral-tribal-affiliation"
                                class="form-control"
                                type="text"
                                placeholder="Enter Type of Affiliation"
                                v-model="referral.othertribalAffiliation"
                            />
                        </div>
                        <div
                            v-if="
                                referral.isIdentifyWithTribe === true &&
                                !hasDeclinedToIdentifyTribe &&
                                referral.typeOfAffiliation > 0 &&
                                referral.typeOfAffiliation < 4
                            "
                            class="form-group col-12 col-lg-2 ps-2 pt-1 pe-lg-2"
                        >
                            <label class="pt-2 mt-1"></label>
                            <MultiSelectFilter
                                id="client-tribe-list"
                                :on-fetch="tribeNameFilter"
                                :selected-items="tribeList"
                                v-on:selectedItemsUpdated="tribeListChanged"
                                placeholder="search tribe name"
                                customMessage="Tribe Name"
                            />
                        </div>
                    </template>
                    <div class="col-12">
                        <h6 class="mb-2 d-flex align-items-center">
                            <b class="me-2">Patient Primary Address</b>
                        </h6>
                    </div>
                    <div v-if="loading"><span class="shimmer-loading"></span></div>
                    <template v-else>
                        <ReferralAddressForm
                            address-type="primaryAddress"
                            v-model:facility-id="referral.primaryAddressFacilityTypeId"
                            v-model:address="referral.primaryAddress"
                            :application="referral.application"
                            :account-id="referral.accountId"
                            :show-code="isHisSection"
                            :is-primary-address="true"
                            :reset-same-as-primary-address="resetSameAsPrimaryAddress"
                            :is-address-validation-in-progress="isAddressValidationInProgress"
                            :provider-id="providerId"
                        />
                        <div class="col-12 d-flex align-items-center">
                            <div class="me-2 ai-validate-address-btn">
                                <button
                                    class="button fr ai-validate-btn"
                                    @click="validateAddress()"
                                    v-if="!referral.primaryAddress.latitude && !referral.primaryAddress.longitude"
                                >
                                    Validate Patient Primary Address
                                </button>
                            </div>
                            <div>
                                <h6
                                    :class="`${
                                        referral.primaryAddress.isBypassed ? 'text-warning' : 'text-danger'
                                    } fw-bold`"
                                    v-if="!referral.primaryAddress.latitude && !referral.primaryAddress.longitude"
                                >
                                    <i class="fas fa-exclamation-triangle"></i>
                                    <span>
                                        Address
                                        {{
                                            referral.primaryAddress.isBypassed ? "Validation Bypassed" : "Not Validated"
                                        }}</span
                                    >
                                </h6>
                                <h6 class="text-success fw-bold" v-else>
                                    <i class="fas fa-check-circle"></i>
                                    <span> Address Validated </span>
                                </h6>
                            </div>
                        </div>
                    </template>
                    <div class="col-12 mt-2">
                        <h6 class="mb-2 d-flex align-items-center">
                            <b class="me-2">Patient Visit Address</b>
                        </h6>
                    </div>

                    <div v-if="loading"><span class="shimmer-loading"></span></div>
                    <template v-else>
                        <div class="col-12 form-group d-flex">
                            <div class="form-check custom-checkbox form-check-inline">
                                <input
                                    type="checkbox"
                                    id="visit-same-primary-address"
                                    class="form-check-input au-target"
                                    v-model="visitAddrSameAsPrimaryAddr"
                                />
                                <label class="form-check-label" for="visit-same-primary-address">
                                    <span class="me-1">Same as Primary Address</span>
                                </label>
                            </div>
                        </div>
                        <ReferralAddressForm
                            v-if="!visitAddrSameAsPrimaryAddr"
                            address-type="visitAddress"
                            :address="referral.visitAddress"
                            :application="referral.application"
                            :account-id="referral.accountId"
                            :facility-id="referral.visitAddressFacilityTypeId"
                            :provider-id="providerId"
                        />
                    </template>
                    <div class="col-12 mt-2">
                        <h6 class="mb-2 d-flex align-items-center">
                            <b class="me-2">Patient Mailing Address</b>
                        </h6>
                    </div>

                    <div v-if="loading"><span class="shimmer-loading"></span></div>
                    <template v-else>
                        <div class="col-12 form-group d-flex">
                            <div class="form-check custom-checkbox form-check-inline">
                                <input
                                    type="checkbox"
                                    id="mailing-same-primary-address"
                                    class="form-check-input au-target"
                                    v-model="mailingAddrSameAsPrimaryAddr"
                                />
                                <label class="form-check-label" for="mailing-same-primary-address">
                                    <span class="me-1">Same as Primary Address</span>
                                </label>
                            </div>
                        </div>
                        <ReferralAddressForm
                            v-if="!mailingAddrSameAsPrimaryAddr"
                            address-type="mailingAddress"
                            :address="referral.mailingAddress"
                            :application="referral.application"
                            :account-id="referral.accountId"
                            :facility-id="referral.mailingAddressFacilityTypeId"
                            :provider-id="providerId"
                        />
                    </template>
                    <template v-if="isApplicationPalliative">
                        <div class="col-12 mt-2">
                            <h6 class="mb-2 d-flex align-items-center">
                                <b class="me-2">Patient Previous Address</b>
                            </h6>
                        </div>
                        <div v-if="loading"><span class="shimmer-loading"></span></div>
                        <template v-else>
                            <div class="col-12 form-group d-flex">
                                <div class="form-check custom-checkbox form-check-inline">
                                    <input
                                        type="checkbox"
                                        id="previous-same-primary-address"
                                        class="form-check-input au-target"
                                        v-model="referral.hasPreviousAddress"
                                    />
                                    <label class="form-check-label" for="previous-same-primary-address">
                                        <span class="me-1">Has Previous Address</span>
                                    </label>
                                </div>
                            </div>
                            <ReferralAddressForm
                                v-if="referral.hasPreviousAddress"
                                address-type="previousAddress"
                                :address="referral.previousAddress"
                                :application="referral.application"
                                :account-id="referral.accountId"
                                :facility-id="referral.previousAddressFacilityTypeId"
                                :provider-id="providerId"
                                :show-move-date="true"
                            />
                        </template>
                    </template>
                </div>
            </div>
            <div class="row">
                <div class="form-group col-12 col-lg-4 pe-lg-2">
                    <label id="Referral_Cahps_Label" for="referral-Cahps"> CAHPS Survey </label>
                    <select
                        id="Referral_Cahps"
                        class="form-select"
                        name="referral-Cahps"
                        v-model="referral.cahpsSurveyContactType"
                    >
                        <option :value="0">Receive CAHPS Survey</option>
                        <option :value="1">No Publicity</option>
                    </select>
                </div>
            </div>
            <slot name="languages"> </slot>
            <slot name="tags"> </slot>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./DemographicsInformation.ts"></script>
<style src="./DemographicsInformation.scss"></style>
